/*
 * Copyright (c) 2025 curryfirm.com
 */
/*
 * Copyright (c) 2025 curryfirm.com
 */

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    selectTotalLength,
    selectMoves,
    selectRoutes,
    selectSteps,
    selectRoutingError,
    selectRoutingIsLoading,
    computeRouteThunk,
} from "../../../features/routing/routingSlice.js";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { GOOGLE_MAPS_KEY } from "../../../constants.js";


// Expect onBack to be passed in as a prop
const DisplayRoute = ({ onBack }) => {
    const dispatch = useDispatch();

    const length = useSelector(selectTotalLength);
    const moves = useSelector(selectMoves);
    const routes = useSelector(selectRoutes);
    const steps = useSelector(selectSteps);
    const loading = useSelector(selectRoutingIsLoading);
    const error = useSelector(selectRoutingError);

    useEffect(() => {
        dispatch(computeRouteThunk());
    }, [dispatch]);

    const [tabIndex, setTabIndex] = useState(0);

    const getPolyline = () => routes?.[0]?.polyline?.encodedPolyline;
    const startLatLng = `${moves?.[0]?.from?.lat},${moves?.[0]?.from?.lng}`;
    const endInx = moves?.length - 1;
    const endLatLng = `${moves?.[endInx]?.to?.lat},${moves?.[endInx]?.to?.lng}`;

    const getRouteImageUrlFromPolyline = (encodedPolyline, startLatLng, endLatLng) => {
        const width = 600
        const height = 400;
        // Main path
        const pathParam = `path=enc:${encodedPolyline}`;
        // Start and end markers
        const markers = [
            `markers=color:green|label:S|${startLatLng}`,
            `markers=color:red|label:E|${endLatLng}`
        ].join('&');
        return `https://maps.googleapis.com/maps/api/staticmap?size=${width}x${height}&${pathParam}&${markers}&key=${GOOGLE_MAPS_KEY}`;
    }

    const MovesTab = () => {
        return (
            <>
                <Typography variant="h6" sx={{ mt: 2 }}>
                    Total Travel Time:{' '}
                    {length ? ` ${(length / 60).toFixed(0)} minutes` : " N/A"}
                </Typography>

                {moves?.length > 0 ? (
                    <Box sx={{ mt: 2 }}>
                        <TableContainer component={Paper}>
                            <Table size="small" aria-label="route moves">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center">Move</TableCell>
                                        <TableCell>From</TableCell>
                                        <TableCell align="center">Action</TableCell>
                                        <TableCell>To</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {moves.map((move, i) => (
                                        <TableRow key={i}>
                                            <TableCell align="center">{i + 1}</TableCell>
                                            <TableCell>{(move.from?.customer ? move.from.customer + ', ' : '') + move.from?.street || "Unknown"}</TableCell>
                                            <TableCell align="center" sx={{ color: "#1976d2", fontWeight: 600 }}>
                                                {move.action}
                                            </TableCell>
                                            <TableCell>{(move.to?.customer ? move.to.customer + ', ' : '') + move.to?.street || "Unknown"}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                ) : (
                    <Typography variant="body2">
                        No moves available for this route.
                    </Typography>
                )}
            </>
        )
    };

    const numStepsColumns = 4;

    const processedStepRows = Object.values(steps).reduce((acc, step, idx) => {
        if (step?.navigationInstruction?.maneuver === "DEPART") {
            acc.customIdx = (acc.customIdx ?? -1) + 1;
            acc.push(
                <TableRow key={`custom-${acc.customIdx}`}>
                    <TableCell colSpan={numStepsColumns}>
                        Move {acc.customIdx + 1}: {' From ' + moves?.[acc.customIdx]?.from?.street + ', ' + moves?.[acc.customIdx]?.from?.city + '; '}
                        <strong>{moves?.[acc.customIdx]?.action}</strong>
                        {': ' + moves?.[acc.customIdx]?.to?.street + ', ' + moves?.[acc.customIdx]?.to?.city}
                    </TableCell>
                </TableRow>
            )
        }
        acc.push(
            <TableRow key={idx}>
                <TableCell align="center">{idx + 1}</TableCell>
                <TableCell align="left" sx={{fontWeight: 600}}>{step?.navigationInstruction?.maneuver || "Unknown"}</TableCell>
                <TableCell align="left">{step?.navigationInstruction?.instructions || "Unknown"}</TableCell>
                <TableCell align="left">{step?.localizedValues?.distance?.text || "Unknown"}</TableCell>
            </TableRow>
        );
        return acc;
    }, []);

    const StepsTab = () => {
        return (
            Object.keys(steps).length > 0 ? (
                <Box sx={{mt: 2}}>
                    <TableContainer component={Paper}>
                        <Table size="small" aria-label="route steps">
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center">Step</TableCell>
                                    <TableCell align="left">Maneuver</TableCell>
                                    <TableCell align="left">Instructions</TableCell>
                                    <TableCell align="left">Distance</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {processedStepRows}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            ) : (
                <Typography variant="body2">
                    No steps available for this route.
                </Typography>
            )
        );
    }

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    return (
        <Box sx={{ mt: 4 }}>
            <Typography variant="h4" gutterBottom>
                Optimal Route
            </Typography>

            {loading && (
                <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
                    <CircularProgress size={28} />
                    <Typography sx={{ ml: 2 }}>Computing route…</Typography>
                </Box>
            )}

            {error && !loading && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}

            {!loading && !error && (
                <>
                    <Tabs
                        value={tabIndex}
                        onChange={handleTabChange}
                        indicatorColor={"primary"}
                        textColor={"primary"}
                        variant={"fullWidth"}
                        sx={{ mb: 2 }}
                    >
                        <Tab label={"Moves"} />
                        <Tab label={"Map"} />
                        <Tab label={"Steps"} />
                    </Tabs>

                    {tabIndex === 0 && (
                        <Box p={2}>
                            <MovesTab />
                        </Box>
                    )}

                    {tabIndex === 1 && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                            {(getPolyline() && startLatLng && endLatLng) ? (
                                <img
                                    src={getRouteImageUrlFromPolyline(getPolyline(), startLatLng, endLatLng)}
                                    alt={"Route map"}
                                    style={{ borderRadius: 8, maxWidth: "100%", height: "auto" }}
                                />
                            ) : (
                                <Typography variant="body2" sx={{ mt: 2 }}>
                                    [Map view unavailable]
                                </Typography>
                            )}
                        </Box>
                    )}

                    {tabIndex === 2 && (
                        <Box p={2}>
                            <StepsTab />
                        </Box>
                    )}
                </>
            )}

            <Box sx={{ mt: 3 }}>
                <Button variant="contained" onClick={onBack}>
                    Back
                </Button>
            </Box>
        </Box>
    );
};

export default DisplayRoute;
